import type { GroupBy, ViewFilters } from "../../domain/app-types.ts";
import type { SortDirection, TaskSort } from "../../domain/entities.ts";
import { filtersFromForm, savedViewDialogView, savedViewManagerView, type SavedViewDialogModel } from "./saved-view-dialog-view.ts";
import { updateSortOrderAvailability } from "../../ui/settings-feedback.ts";
import { bindActions } from "../../app/actions.ts";
import { customFieldValue } from "../custom-fields/model.ts";

export interface SavedViewSubmission {
  mode: "apply" | "save";
  filters: ViewFilters;
  groupBy: GroupBy;
  sortBy: TaskSort;
  sortDirection: SortDirection;
  pageSize: number;
  name: string;
}

export interface SavedViewDialogCapabilities {
  model(): SavedViewDialogModel;
  clear(): Promise<void>;
  deleteView(id: string): Promise<void>;
  duplicateView(id: string): Promise<void>;
  publishView(id: string): Promise<void>;
  toggleDefault(id: string): Promise<void>;
  moveView(id: string, direction: "up" | "down"): Promise<void>;
  applyView?(id: string): Promise<void>;
  submit(submission: SavedViewSubmission): Promise<boolean>;
}

export function openSavedViewDialog(capabilities: SavedViewDialogCapabilities): void {
  const previous = document.activeElement as HTMLElement | null;
  const menuTrigger = previous?.closest("details.composer-menu")?.querySelector<HTMLElement>("summary");
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog filter-dialog";
  dialog.setAttribute("aria-label", "Task view options");
  let busy = false;
  const run = async (operation: () => Promise<void>): Promise<void> => {
    if (busy) return;
    busy = true;
    const buttons = [...dialog.querySelectorAll<HTMLButtonElement>("button:not(:disabled)")];
    buttons.forEach(button => { button.disabled = true; });
    try { await operation(); } catch {
      const error = dialog.querySelector<HTMLElement>("[data-view-management] .review-error, form:not([hidden]) .review-error");
      if (error) { error.textContent = "Could not save this change. Please try again."; error.hidden = false; }
    } finally { busy = false; buttons.forEach(button => { button.disabled = false; }); }
  };
  const manage = (focus?: { attribute: string; id: string; direction?: string }): void => {
    const form = dialog.querySelector<HTMLFormElement>("form")!;
    form.hidden = true;
    dialog.querySelector("[data-view-management]")?.remove();
    dialog.insertAdjacentHTML("beforeend", savedViewManagerView(capabilities.model()));
    const manager = dialog.querySelector<HTMLElement>("[data-view-management]")!;
    bindActions(manager, () => undefined);
    manager.querySelectorAll<HTMLElement>("[data-apply-view]").forEach(button => button.addEventListener("click", () => { void run(async () => { await capabilities.applyView?.(button.dataset.applyView!); dialog.close(); }); }));
    manager.querySelector("[data-manager-close]")?.addEventListener("click", () => dialog.close());
    manager.querySelector("[data-manager-back]")?.addEventListener("click", () => { manager.remove(); form.hidden = false; form.querySelector<HTMLElement>("[data-view-manager]")?.focus(); });
    for (const [attribute, action] of Object.entries({ "delete-view": capabilities.deleteView, "duplicate-view": capabilities.duplicateView, "publish-view": capabilities.publishView, "default-view": capabilities.toggleDefault })) {
      manager.querySelectorAll<HTMLElement>(`[data-${attribute}]`).forEach(button => button.addEventListener("click", () => { void run(async () => { const id = button.getAttribute(`data-${attribute}`)!; await action(id); if (attribute === "delete-view") dialog.close(); else manage({ attribute, id }); }); }));
    }
    manager.querySelectorAll<HTMLElement>("[data-move-view]").forEach(button => button.addEventListener("click", () => { void run(async () => { const id = button.dataset.moveView!, direction = button.dataset.direction === "up" ? "up" : "down"; await capabilities.moveView(id, direction); manage({ attribute: "move-view", id, direction }); }); }));
    const previous = focus && [...manager.querySelectorAll<HTMLButtonElement>(`[data-${focus.attribute}]`)].find(button => button.getAttribute(`data-${focus.attribute}`) === focus.id && (!focus.direction || button.dataset.direction === focus.direction) && !button.disabled);
    (previous?.closest("details")?.querySelector<HTMLElement>("summary") || previous || manager.querySelector<HTMLElement>("[data-manager-back]"))?.focus();
  };
  const render = () => {
    dialog.innerHTML = savedViewDialogView(capabilities.model());
    const updateFieldFilter = (row: Element) => {
      const id = row.querySelector<HTMLSelectElement>('[name="customFilterId"]')!.value;
      const field = capabilities.model().customFields?.find(field => field.id === id);
      const operator = row.querySelector<HTMLSelectElement>('[name="customFilterOperator"]')!;
      for (const option of operator.options) option.disabled = !!field && (["date", "number"].includes(field.kind) ? option.value === "contains" : ["gte", "lte"].includes(option.value));
      if (operator.selectedOptions[0]?.disabled) operator.value = "set";
      (operator.closest("label") as HTMLElement).hidden = !id;
      const value = row.querySelector<HTMLInputElement>('[name="customFilterValue"]')!;
      (value.closest("label") as HTMLElement).hidden = !id || ["set", "unset"].includes(operator.value);
      value.setCustomValidity("");
    };
    dialog.querySelectorAll(".custom-filter-row").forEach(updateFieldFilter);
    dialog.addEventListener("change", event => {
      const control = event.target as HTMLInputElement | HTMLSelectElement;
      if (["customFilterId", "customFilterOperator"].includes(control.name)) { const row = control.closest(".custom-filter-row"); if (row) updateFieldFilter(row); }
    });
    dialog.addEventListener("input", event => { const control = event.target as HTMLInputElement; if (control.name === "customFilterValue") control.setCustomValidity(""); });
    dialog.querySelector("[data-filter-close]")!.addEventListener("click", () => dialog.close());
    dialog.querySelector("[data-view-manager]")?.addEventListener("click", () => manage());
    dialog.querySelector("[data-filter-save-options]")?.addEventListener("click", () => {
      const section = dialog.querySelector<HTMLElement>("[data-filter-save-section]")!;
      section.hidden = false;
      section.querySelector<HTMLInputElement>("input")?.focus();
    });
    const sortSelect = dialog.querySelector<HTMLSelectElement>("[name=sortBy]")!;
    const scope = dialog.querySelector<HTMLSelectElement>('[name="spaceScope"]');
    const updateScope = () => {
      const selected = dialog.querySelector<HTMLElement>("[data-selected-spaces]"); if (selected) selected.hidden = scope?.value !== "selected";
      const collections = dialog.querySelector<HTMLElement>("[data-scope-collections]"); if (collections) collections.hidden = !["all", "selected"].includes(scope?.value || "");
    };
    scope?.addEventListener("change", updateScope); updateScope();
    const fieldSection = dialog.querySelector(".custom-filter-row")?.parentElement;
    if (fieldSection) {
      const add = document.createElement("button"); add.type = "button"; add.className = "text-button"; add.textContent = "Add field filter";
      add.addEventListener("click", () => {
        const rows = fieldSection.querySelectorAll<HTMLElement>(".custom-filter-row");
        if (rows.length >= 10) return;
        const row = rows[rows.length - 1].cloneNode(true) as HTMLElement;
        row.querySelectorAll<HTMLSelectElement>("select").forEach(control => { control.selectedIndex = 0; }); row.querySelector<HTMLInputElement>("input")!.value = "";
        add.before(row); updateFieldFilter(row); row.querySelector<HTMLElement>("select")?.focus(); add.disabled = rows.length + 1 >= 10;
      });
      add.disabled = fieldSection.querySelectorAll(".custom-filter-row").length >= 10; fieldSection.append(add);
    }
    const directionSelect = dialog.querySelector<HTMLSelectElement>("[name=sortDirection]")!;
    const orderHint = dialog.querySelector<HTMLElement>("#filter-sort-order-hint")!;
    const updateSortDirection = () => updateSortOrderAvailability(sortSelect, directionSelect, orderHint);
    sortSelect.addEventListener("change", updateSortDirection); updateSortDirection();
    dialog.querySelector("[data-clear-filters]")!.addEventListener("click", () => { void run(async () => { await capabilities.clear(); dialog.close(); }); });
    dialog.querySelector<HTMLFormElement>("form")!.addEventListener("submit", event => {
      event.preventDefault();
      for (const row of dialog.querySelectorAll(".custom-filter-row")) {
        const id = row.querySelector<HTMLSelectElement>('[name="customFilterId"]')!.value;
        const operator = row.querySelector<HTMLSelectElement>('[name="customFilterOperator"]')!.value;
        if (!id || ["set", "unset"].includes(operator)) continue;
        const input = row.querySelector<HTMLInputElement>('[name="customFilterValue"]')!;
        const field = capabilities.model().customFields?.find(field => field.id === id);
        input.setCustomValidity("");
        try { if (!input.value.trim()) throw new Error("Add a comparison value, or choose a presence condition."); if (field && operator !== "contains") customFieldValue(field, input.value); }
        catch (error) { input.setCustomValidity(error instanceof Error ? error.message : "Invalid comparison value."); }
        if (!input.checkValidity()) { const section = row.closest("details"); if (section) section.open = true; input.reportValidity(); return; }
      }
      const data = new FormData(event.currentTarget as HTMLFormElement);
      if (data.get("spaceScope") === "selected" && !data.getAll("spaceIds").length) {
        const error = dialog.querySelector<HTMLElement>("form .review-error")!; error.textContent = "Select at least one space."; error.hidden = false; scope?.focus(); return;
      }
      if (data.get("spaceScope") !== "selected") data.delete("spaceIds");
      if (!["all", "selected"].includes(String(data.get("spaceScope")))) data.delete("collectionIds");
      const submitter = (event as SubmitEvent).submitter as HTMLButtonElement | null;
      if (submitter?.dataset.submitMode === "save" && !String(data.get("viewName") || "").trim()) {
        const error = dialog.querySelector<HTMLElement>("form .review-error")!;
        error.textContent = "Add a name before saving this view."; error.hidden = false;
        dialog.querySelector<HTMLInputElement>('[name="viewName"]')?.focus(); return;
      }
      void run(async () => {
      const close = await capabilities.submit({
        mode: submitter?.dataset.submitMode === "save" ? "save" : "apply",
        filters: filtersFromForm(data),
        groupBy: data.get("groupBy") as GroupBy,
        sortBy: String(data.get("sortBy") || "manual") as TaskSort,
        sortDirection: String(data.get("sortDirection") || "asc") as SortDirection,
        pageSize: Number(data.get("pageSize")),
        name: String(data.get("viewName") || "").trim(),
      });
      if (close) dialog.close();
      });
    });
  };
  render(); document.body.append(dialog);
  dialog.addEventListener("close", () => {
    document.querySelector("#toast-region")?.append(...dialog.querySelectorAll(".dialog-toast-region > .toast"));
    dialog.remove();
    const trigger = menuTrigger?.isConnected ? menuTrigger : previous?.isConnected && previous !== document.body ? previous : document.querySelector<HTMLElement>("[data-task-filter-trigger]");
    trigger?.focus({ preventScroll: true });
  });
  dialog.showModal();
}
